const ON_PREMISE = ['bar', 'restaurant'];
const OFF_PREMISE = ['store', 'entertainment', 'other'];
/**
 * Custom HTML element that provides class-of-trade filter functionality.
 * Displays filter buttons for different trade categories (store, restaurant, entertainment, bar, other)
 * and emits events when selections change.
 *
 * @example
 * ```html
 * <awe-cot-filters allowed-trades="store,restaurant,bar"></awe-cot-filters>
 * ```
 *
 * @fires AweClassOfTradeFilters#avails-facet-change - Fired when filter selection changes
 */
class AweClassOfTradeFilters extends HTMLElement {
    /**
     * Creates an instance of AweClassOfTradeFilters.
     * Initializes the shadow DOM, creates the container element, sets up icon mappings,
     * creates the clear button, and applies component styles.
     */
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.container = document.createElement('div');
        this.container.classList.add('cot-ctnr');
        // Define all possible items
        this.allowedTrades = null; // Will be set if attribute is passed
        this.selectedItems = new Set();
        this.simpleView = false;
        // map class-of-trade to icon name
        this.iconMapping = {
            store: 'icon-shop',
            restaurant: 'icon-restaurant',
            entertainment: 'icon-cinema',
            bar: 'icon-glass',
            other: 'icon-dot-3',
        };
        // Clear button
        const btn = document.createElement('button');
        btn.classList.add('clear-button');
        btn.innerHTML = '<i class=\'icon-cancel\'></i>';
        btn.disabled = true;
        btn.addEventListener('click', this.clearFilters.bind(this));
        btn.setAttribute('aria-label', window.i18n.get('AWE-COT-FILTERS.CLEAR'));
        this.clearButton = btn;
        // Add main styles
        const style = document.createElement('style');
        style.textContent = `@import url(../assets/a5327d8/fonts/fontello.css);.cot-ctnr{display:flex;flex-wrap:nowrap;overflow-x:auto;padding-bottom:10px;gap:10px}.cot-ctnr.cot-simple-view{justify-content:flex-start}.cot-filter{display:flex;align-items:center;flex:0 0 auto}input{display:none}button,label{color:var(--btn-toggle-color,#000);background-color:var(--cot-bg-color,#fff);border:1px solid var(--cot-border-color,var(--border-color));border-radius:var(--border-radius,0);padding:5px 10px;cursor:pointer}label{display:flex;align-items:center;gap:.4em}.layout-icon label{gap:0}.cot-filter input:checked+label{background-color:var(--btn-toggle-color);color:#fff}button:disabled{opacity:.1;cursor:not-allowed}`;
        this.shadowRoot?.append(style, this.container);
    }
    /**
     * Specifies which attributes should be observed for changes.
     * When these attributes change, attributeChangedCallback will be called.
     *
     * @returns {string[]} Array containing the attribute names to observe
     */
    static get observedAttributes() {
        return ['allowed-trades', 'bar-icon', 'layout', 'simple-view'];
    }
    /**
     * Called when one of the observed attributes changes.
     * Currently handles the 'allowed-trades' attribute by parsing the comma-separated
     * list of trade types and updating the filter display.
     *
     * @param {string} name - The name of the attribute that changed
     * @param {string} _ - The old value (unused)
     * @param {string} newValue - The new value of the attribute
     */
    attributeChangedCallback(name, _, newValue) {
        try {
            switch (name) {
                case 'allowed-trades':
                    this.allowedTrades = newValue.split(',');
                    break;
                case 'bar-icon':
                    this.iconMapping.bar = `icon-${newValue || 'glass'}`;
                    break;
                case 'layout':
                    this.layout = newValue;
                    break;
                case 'simple-view': {
                    const nextSimpleView = newValue != null && newValue !== 'false';
                    if (this.simpleView !== nextSimpleView) {
                        this.selectedItems.clear();
                        this.simpleView = nextSimpleView;
                    }
                    break;
                }
            }
            this.updateFilters();
        }
        catch (err) {
            console.error('Error updating COT filters:', err); // eslint-disable-line no-console
        }
    }
    /**
     * Called when the element is inserted into the DOM.
     * Ensures the component is properly connected before updating filters.
     */
    connectedCallback() {
        if (!this.isConnected)
            return;
        if (this.hasAttribute('layout'))
            this.layout = this.getAttribute('layout');
        if (this.hasAttribute('simple-view')) {
            const simpleView = this.getAttribute('simple-view');
            this.simpleView = simpleView !== 'false';
        }
        this.updateFilters();
    }
    /**
     * Returns enabled trades from a group that are present in allowedTrades.
     *
     * @private
     */
    getEnabledTradesInGroup(group) {
        return group.filter((trade) => this.allowedTrades?.includes(trade));
    }
    /**
     * Creates a filter checkbox with label and keyboard support.
     *
     * @private
     */
    createFilterElement(id, ariaLabelKey, labelKey, iconClass, isChecked, groupTrades) {
        const div = document.createElement('div');
        div.classList.add('cot-filter');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        if (groupTrades)
            input.dataset.trades = groupTrades.join(',');
        else
            input.value = id.replace(/^cot-/, '');
        const label = document.createElement('label');
        label.setAttribute('for', input.id);
        label.setAttribute('tabindex', '0');
        label.setAttribute('aria-label', window.i18n.get(ariaLabelKey));
        label.append(...this.buildLabel(labelKey, iconClass));
        label.addEventListener('keypress', (e) => {
            if (e.key !== 'Enter' || e.repeat)
                return;
            const labelNd = e.currentTarget;
            const inputNd = labelNd.previousElementSibling;
            inputNd?.click();
        });
        input.checked = isChecked;
        input.addEventListener('change', this.toggleSelection.bind(this));
        div.append(input, label);
        return div;
    }
    /**
     * Builds and updates the list of filter buttons based on allowed trades.
     * Creates DOM elements for each trade type with checkboxes, labels, and icons.
     * Also sets up event handlers for keyboard navigation and selection changes.
     *
     * @private
     */
    updateFilters() {
        const elements = [];
        this.container.classList.toggle('cot-simple-view', this.simpleView);
        if (this.simpleView) {
            const onPremiseTrades = this.getEnabledTradesInGroup(ON_PREMISE);
            if (onPremiseTrades.length > 0) {
                elements.push(this.createFilterElement('cot-on-premise', 'AWE-COT-FILTERS.LABEL_ON_PREMISE', 'AWE-COT-FILTERS.LABEL_ON_PREMISE', this.iconMapping.bar, onPremiseTrades.every((trade) => this.selectedItems.has(trade)), onPremiseTrades));
            }
            const offPremiseTrades = this.getEnabledTradesInGroup(OFF_PREMISE);
            if (offPremiseTrades.length > 0) {
                elements.push(this.createFilterElement('cot-off-premise', 'AWE-COT-FILTERS.LABEL_OFF_PREMISE', 'AWE-COT-FILTERS.LABEL_OFF_PREMISE', this.iconMapping.store, offPremiseTrades.every((trade) => this.selectedItems.has(trade)), offPremiseTrades));
            }
        }
        else {
            this.allowedTrades?.forEach((trade) => {
                elements.push(this.createFilterElement(`cot-${trade}`, `AWE-COT-FILTERS.${trade.toUpperCase()}`, `AWE-COT-FILTERS.LABEL_${trade.toUpperCase()}`, this.iconMapping[trade], this.selectedItems.has(trade)));
            });
        }
        this.container.innerHTML = '';
        this.container.append(...elements, this.clearButton);
        this.clearButton.disabled = this.selectedItems.size === 0;
    }
    /** @param value - Raw layout attribute value; invalid values are ignored */
    set layout(value) {
        if (value !== 'icon' && value !== 'text' && value !== 'icon+text')
            return;
        this.container.classList.remove('layout-' + this._layout);
        this._layout = value;
        this.container.classList.add(`layout-${value}`);
    }
    /** Returns the current layout, or undefined if none has been set. */
    get layout() {
        return this._layout;
    }
    /**
     * Builds the child elements for a filter label based on the current layout.
     *
     * @param {string} labelKey - i18n key for the visible label text
     * @param {string} iconClass - Icon class name for the filter button
     * @returns {HTMLElement[]} Array of 1 or 2 elements (icon and/or text span) to append to the label
     * @private
     */
    buildLabel(labelKey, iconClass) {
        const elements = [];
        if (this.layout !== 'text') {
            const icon = document.createElement('i');
            icon.classList.add(iconClass);
            elements.push(icon);
        }
        if (this.layout !== 'icon') {
            const text = document.createElement('span');
            text.textContent = window.i18n.get(labelKey);
            elements.push(text);
        }
        return elements;
    }
    /**
     * Event handler called when a filter checkbox is selected or deselected.
     * Updates the internal selectedItems set and dispatches a change event.
     * Also manages the state of the clear button.
     *
     * @param {Event} evt - The change event from the checkbox input
     * @private
     */
    toggleSelection(evt) {
        const target = evt.currentTarget;
        const groupTrades = target.dataset.trades?.split(',');
        if (groupTrades) {
            if (target.checked)
                groupTrades.forEach((trade) => this.selectedItems.add(trade));
            else
                groupTrades.forEach((trade) => this.selectedItems.delete(trade));
        }
        else {
            const cot = target.value;
            if (target.checked)
                this.selectedItems.add(cot);
            else
                this.selectedItems.delete(cot);
        }
        this.dispatchFilterChange();
        this.clearButton.disabled = this.selectedItems.size === 0;
    }
    /**
     * Dispatches a custom event to notify listeners of filter changes.
     * Emits an 'avails-facet-change' event with the current selection state.
     *
     * @fires AweClassOfTradeFilters#avails-facet-change
     * @private
     */
    dispatchFilterChange() {
        this.dispatchEvent(new CustomEvent('avails-facet-change', {
            bubbles: true,
            detail: {
                key: 'class-of-trade',
                values: Array.from(this.selectedItems),
            },
        }));
    }
    /**
     * Clears all selected filters and resets the component state.
     * Unchecks all checkbox inputs, clears the selectedItems set,
     * and dispatches a change event to notify listeners.
     */
    clearFilters() {
        this.selectedItems.clear();
        const inputs = this.container.querySelectorAll('.cot-filter input');
        for (const input of inputs) {
            input.checked = false;
        }
        this.dispatchFilterChange();
        this.clearButton.disabled = true;
    }
}
customElements.define('awe-cot-filters', AweClassOfTradeFilters);
// on filters change event, update availabilities
const filtersNd = document.getElementsByTagName('awe-cot-filters')[0];
if (filtersNd) {
    filtersNd.addEventListener('avails-facet-change', (e) => {
        const customEvent = e;
        player.getAvails(false, { classOfTrade: customEvent.detail.values });
    });
}
